<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import { ContentsContext } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import UserDocumentOpenPreviewModalButton from '@/components/user-document/user-document-open-preview-modal-button.vue';
import {
  UserDocumentEntity,
  UserDocumentEventName,
  UserDocumentViewType,
  UserDocumentViewWithParentInfo,
} from '@/types';
import { useUserDocumentActionHistories } from '@/utils/composables/useUserDocumentActionHistories';
import {
  getDocumentUrl,
  getFileTypeIconName,
  getFolderName,
  getModifiedAt,
  getStorageType,
} from '@/utils/user-document/common';
import { userSession } from '@/utils/userSession';
import { isDev } from '@/featureFlags';

interface Props {
  userDocumentSimilarDocument: UserDocumentEntity;
  parentUserDocument: UserDocumentEntity;
  parentRank: number | undefined;
  rankInParent: number;
  contentsContext: ContentsContext;
}

const clickUserDocumentTitle = async () => {
  await createUserDocumentViewHistories(props.userDocumentSimilarDocument.id);
  await api.trackingUserDocumentEvent(
    'user_document_view',
    userDocumentViewInfo('original'),
  );
};

const trackingSimilarUserDocumentEvent = async (
  eventName: UserDocumentEventName,
) =>
  await api.trackingUserDocumentEvent(eventName, {
    page: {
      name: 'user_document_search',
    },
    feature: 'user_document_search',
    rank: props.rankInParent,
    user_document: {
      id: props.userDocumentSimilarDocument.id,
    },
    parent_user_document: {
      rank: props.parentRank,
      user_document: {
        id: props.parentUserDocument.id,
      },
    },
    contents_context: props.contentsContext as Extract<
      ContentsContext,
      { event_name: 'user_document_search' }
    >,
    view_type: 'original',
  } as UserDocumentViewWithParentInfo);

const props = defineProps<Props>();
const emit = defineEmits<{
  'select-file': [id: string];
}>();
const route = useRoute();
const { createUserDocumentViewHistories } = useUserDocumentActionHistories(
  userSession.getUserId(),
);
const userDocumentSimilarDocument = computed(
  () => props.userDocumentSimilarDocument,
);
const fileTypeIcon = computed(() =>
  getFileTypeIconName(props.userDocumentSimilarDocument.file_type),
);
const folderName = computed(() =>
  getFolderName(props.userDocumentSimilarDocument),
);
const documentUrl = computed(() =>
  getDocumentUrl(props.userDocumentSimilarDocument),
);
const modifiedAt = computed(() =>
  getModifiedAt(props.userDocumentSimilarDocument),
);

/**
 * デバッグ用の判定項目
 *
 * dev環境かつ、クエリパラメータに該当の項目がある場合に、
 * ストレージ名 (box, share_point, Anewsストレージ) を表示する
 */
const showStorageType = computed(() => {
  if (!isDev) return false;

  return route.query?.showStorageType === 'true';
});

const userDocumentViewInfo = (
  viewType: UserDocumentViewType,
): UserDocumentViewWithParentInfo => ({
  page: {
    name: 'user_document_search',
  },
  feature: 'user_document_search',
  rank: props.rankInParent,
  user_document: {
    id: props.userDocumentSimilarDocument.id,
  },
  parent_user_document: {
    rank: props.parentRank,
    user_document: {
      id: props.parentUserDocument.id,
    },
  },
  contents_context: props.contentsContext as Extract<
    ContentsContext,
    { event_name: 'user_document_search' }
  >,
  view_type: viewType,
});
</script>

<template>
  <div class="user-document-similar-document-card">
    <div class="user-document-content">
      <div class="user-document-data-source-info c-text-regular--xs">
        <div class="user-document-page-number">
          <div>
            <DgrIcon :name="fileTypeIcon" />
          </div>
          <div class="spacing-02"></div>
          <div class="page-number">
            ページ：{{ userDocumentSimilarDocument.page_number }}
          </div>
        </div>
        <div class="spacing-04"></div>
        <component
          :is="userDocumentSimilarDocument.folder_url ? 'a' : 'span'"
          :href="userDocumentSimilarDocument.folder_url"
          target="_blank"
          rel="noopener noreferrer"
          class="user-document-data-source-name"
          @click="trackingSimilarUserDocumentEvent('user_document_folder_view')"
        >
          <DgrIcon :keep-fill="false" name="folder" class="folder-icon" />
          <div class="spacing-04"></div>
          <div class="user-document-data-source-name-text">
            {{ folderName }}
          </div>
        </component>
      </div>
      <div class="spacing-04"></div>
      <div class="user-document-title c-title-bold--xm">
        <a
          :href="documentUrl"
          target="_blank"
          rel="noopener noreferrer"
          @click="clickUserDocumentTitle()"
        >
          {{ userDocumentSimilarDocument.title }}
        </a>
      </div>
      <div class="spacing-04"></div>
      <div class="user-document-update-info">
        <div class="c-text-regular--s">更新日：{{ modifiedAt }}</div>
        <div class="c-text-regular--s">
          更新者：{{ userDocumentSimilarDocument.last_updated_by }}
        </div>
        <div class="c-text-regular--s" v-if="showStorageType">
          ストレージ: {{ getStorageType(userDocumentSimilarDocument.url) }}
        </div>
      </div>
      <div class="action-buttons">
        <UserDocumentOpenPreviewModalButton
          :id="userDocumentSimilarDocument.id"
          :title="userDocumentSimilarDocument.filename"
          :target-page="userDocumentSimilarDocument.page_number"
          :file-total-pages="userDocumentSimilarDocument.file_total_pages"
          :preview-image-path-template="
            userDocumentSimilarDocument.preview_image_path_template
          "
          :user-document-view-info="userDocumentViewInfo('preview')"
        />
        <button
          class="survey-by-spcified-file-button small"
          @click="emit('select-file', userDocumentSimilarDocument.id)"
        >
          <DgrIcon
            name="sparkles-fill"
            size="small"
            :keep-fill="false"
            class="gray-sparkles"
          />
          このファイルから要約を生成
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user-document-similar-document-card {
  width: 100%;
  background-color: white;
  box-sizing: border-box;
  display: flex;

  .user-document-content {
    width: 100%;
    padding: 8px 16px;
    .user-document-update-info {
      display: flex;
      color: $color-gray800;
      font-weight: 400;
      column-gap: 8px;
      padding: 4px 0;
    }
  }
  .user-document-data-source-info {
    padding: 4px 0;
    color: $color-gray800;
    font-weight: 500;
    display: flex;
    align-items: center;
    .user-document-page-number {
      display: flex;
      align-items: center;
      border-right: 1px solid $color-gray400;
      padding-right: 10px;
      .page-number {
        white-space: nowrap;
      }
    }
    .user-document-data-source-name {
      display: flex;
      align-items: center;
      .folder-icon {
        color: $color-gray600;
      }
      .user-document-data-source-name-text {
        overflow: hidden;
        display: -webkit-box;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        line-clamp: 1;
        -webkit-line-clamp: 1;
      }
      &a:hover {
        .user-document-data-source-name-text {
          text-decoration: underline;
        }
      }
    }
  }
  .user-document-title {
    overflow: hidden;
    display: -webkit-box;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    word-break: break-all;
    font-weight: 500;
    a:hover {
      text-decoration: underline;
    }
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }

  .survey-by-spcified-file-button {
    border: none;
    background-color: $color-white;
    padding: 5px 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    &:hover {
      background: $color-gray200;
    }
  }

  .gray-sparkles {
    flex-shrink: 0;
    color: $color-gray1000;
  }
}
</style>
